import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Database, Save, Play, Box, Search, X, ChevronRight, Terminal, Cpu, Info, Code, FileCode } from 'lucide-react';
import toast from 'react-hot-toast';

const ActionScriptStudio = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const editorRef = useRef(null);
    const monacoRef = useRef(null);

    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    // الحالة (State)
    const [schema, setSchema] = useState({});
    const [scriptData, setScriptData] = useState({
        script_name: '',
        object_name: 'PURCHASE',
        script_content: '# Write your logic here\n# Use "source" to access record data\n# Example: if source.totalcost > 1000:\n',
        description: ''
    });
    const [compiling, setCompiling] = useState(false);
    const [consoleLogs, setConsoleLogs] = useState([
        { time: new Date().toLocaleTimeString(), msg: t('studio.console.init'), type: 'info' }
    ]);

    // 1. جلب البيانات (Schema & Script)
    useEffect(() => {
        // جلب الموديلات ديناميكياً من الباكيند
        axios.get('/api/admin/workflow/models-dictionary')
            .then(res => {
                setSchema(res.data || {});
                addLog(t('studio.console.schema_sync'), 'success');
            })
            .catch(err => addLog(t('studio.console.schema_fail'), 'error'));

        // جلب السكربت إذا كان وضع التعديل
        if (id && id !== 'new') {
            axios.get(`/api/admin/workflow/scripts/${id}`)
                .then(res => setScriptData(res.data))
                .catch(err => toast.error(t('scripts.toasts.fetch_fail')));
        }
    }, [id, t]);

    // دالة إضافة اللوجات للكونسول
    const addLog = (msg, type = 'info') => {
        setConsoleLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), msg, type }]);
    };

    // 2. إعداد الـ Intellisense (الاقتراحات التلقائية) - الجزء المفقود
    const handleEditorWillMount = (monaco) => {
        monacoRef.current = monaco;
        
        // تسجيل ميزة الاقتراحات التلقائية (Completion Provider) لـ Python
        monaco.languages.registerCompletionItemProvider('python', {
            triggerCharacters: ['.'],
            provideCompletionItems: (model, position) => {
                const textUntilPosition = model.getValueInRange({
                    startLineNumber: position.lineNumber,
                    startColumn: 1,
                    endLineNumber: position.lineNumber,
                    endColumn: position.column
                });

                // إذا كتب المستخدم source. تظهر له الحقول
                if (textUntilPosition.endsWith('source.')) {
                    const fields = schema[scriptData.object_name]?.fields || [];
                    const suggestions = fields.map(f => ({
                        label: f,
                        kind: monaco.languages.CompletionItemKind.Field,
                        insertText: f,
                        detail: `${t('studio.field_from')} ${scriptData.object_name}`,
                        documentation: t('studio.access_col') + f
                    }));
                    return { suggestions };
                }

                // اقتراحات عامة لـ Object Names عند كتابة مسافة
                if (textUntilPosition.endsWith(' ')) {
                    const suggestions = Object.keys(schema).map(k => ({
                        label: k,
                        kind: monaco.languages.CompletionItemKind.Class,
                        insertText: k,
                        detail: t('studio.db_model')
                    }));
                    return { suggestions };
                }

                return { suggestions: [] };
            }
        });
    };

    // 3. الحفظ والكومبايل
    const handleSave = async () => {
        try {
            const url = id && id !== 'new' 
                ? `/api/admin/workflow/scripts/${id}`
                : '/api/admin/workflow/save-script';
            
            const token = localStorage.getItem('token');
            await axios.post(url, scriptData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(t('studio.toasts.preserved'));
            addLog(t('studio.console.saved_prefix') + ` "${scriptData.script_name}"`, 'success');
            navigate('/admin/workflow/studio');
        } catch (err) { 
            toast.error(t('workflow.toasts.save_fail')); 
            addLog(t('studio.console.error_op'), 'error');
        }
    };

    const runCompile = async () => {
        setCompiling(true);
        addLog(t('studio.console.compiling'));
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/admin/workflow/compile', {
                code: scriptData.script_content,
                object: scriptData.object_name
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(t('studio.toasts.compiled'));
            addLog(t('studio.console.build_finish'), 'success');
        } catch (err) { 
            const errorMsg = err.response?.data?.detail || t('studio.console.syntax_error');
            toast.error(errorMsg);
            addLog(t('studio.console.build_fail') + `: ${errorMsg}`, 'error');
        } finally { setCompiling(false); }
    };

    // 4. حقن الكود عند الكليك (Helper لـ Schema Explorer)
    const insertAtCursor = (text) => {
        const editor = editorRef.current;
        if (editor) {
            const selection = editor.getSelection();
            const op = { range: selection, text: text, forceMoveMarkers: true };
            editor.executeEdits("my-source", [op]);
            editor.focus();
        }
    };

    return (
        <div className={`flex h-screen bg-[#0b0e14] text-slate-300 overflow-hidden select-none ${isRTL ? 'font-cairo' : 'font-sans'}`} dir={isRTL? 'rtl' : 'ltr'}>
            
            {/* Sidebar: Schema Explorer */}
            <div className={`w-80 bg-[#141923] border-slate-800 flex flex-col shadow-2xl ${isRTL ? 'border-l order-2' : 'border-r order-1'}`}>
                <div className={`p-4 bg-[#1c2331] border-b border-slate-800 flex items-center justify-between`}>
                    <h2 className="text-[10px] font-black text-orange-500 uppercase tracking-widest flex items-center gap-2">
                        <Database size={14} /> {t('studio.schema_explorer')}
                    </h2>
                </div>
                
                <div className="p-4 border-b border-slate-800 bg-[#0f131a]">
                    <label className={`text-[9px] font-black text-slate-500 uppercase mb-2 block tracking-widest ${isRTL ? 'text-right' : 'text-left'}`}>{t('studio.target_context')}</label>
                    <select 
                        className={`w-full bg-[#0b0e14] border border-slate-700 rounded p-2 text-xs font-bold outline-none text-blue-400 focus:border-orange-500 transition-all ${isRTL ? 'text-right' : 'text-left'}`}
                        value={scriptData.object_name}
                        onChange={(e) => setScriptData({...scriptData, object_name: e.target.value})}
                    >
                        {Object.keys(schema).map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {schema && Object.entries(schema).map(([model, info]) => (
                        <div key={model} className="border-b border-slate-800/50 text-[11px]">
                            <details className="group" open={model === scriptData.object_name}>
                                <summary className={`p-3 hover:bg-slate-800 cursor-pointer flex justify-between items-center transition-all `}>
                                    <span className={`font-black flex items-center gap-2 tracking-tighter uppercase `}>
                                        <Box size={14} className={model === scriptData.object_name ? "text-orange-500" : "text-slate-500"} />
                                        {model}
                                    </span>
                                    <ChevronRight size={14} className={`transition-transform text-slate-600 group-open:rotate-90 ${isRTL ? 'rotate-180' : ''}`} />
                                </summary>
                                <div className="bg-black/20 p-2 space-y-1">
                                    {info?.fields?.map(field => (
                                        <button 
                                            key={field}
                                            onClick={() => insertAtCursor(`source.${field}`)}
                                            className={`w-full text-left px-3 py-1.5 text-[10px] text-slate-500 hover:text-white hover:bg-orange-500/10 rounded transition-all flex items-center gap-2 group/btn ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                                        >
                                            <div className="w-1 h-1 bg-slate-700 group-hover/btn:bg-orange-500 rounded-full"></div>
                                            {field}
                                        </button>
                                    ))}
                                </div>
                            </details>
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Editor Section */}
            <div className={`flex-1 flex flex-col relative ${isRTL ? 'order-1' : 'order-2'}`}>
                
                {/* Editor Header / Toolbar */}
                <div className={`h-16 bg-[#141923] border-b border-slate-800 flex items-center justify-between px-6 z-10 shadow-lg`}>
                    <div className={`flex items-center gap-4 `}>
                        <div className="bg-orange-500/10 p-2 rounded">
                            <FileCode className="text-orange-500" size={20} />
                        </div>
                        <input 
                            className={`bg-transparent border-b border-slate-700 focus:border-orange-500 outline-none px-2 py-1 text-sm font-bold text-white w-80 transition-all placeholder:text-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}
                            placeholder={t('studio.name_placeholder')}
                            value={scriptData.script_name}
                            onChange={(e) => setScriptData({...scriptData, script_name: e.target.value})}
                        />
                        <div className={`flex items-center gap-2 text-[10px] font-black text-slate-500 bg-black/30 px-3 py-1.5 rounded-full border border-white/5 `}>
                            <Info size={12} className="text-blue-400" /> {t('studio.engine_info')}
                        </div>
                    </div>
                    
                    <div className={`flex items-center gap-3`}>
                        <button 
                            onClick={runCompile}
                            disabled={compiling}
                            className="flex items-center gap-2 px-5 py-2 rounded text-[11px] font-black bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 active:scale-95 disabled:opacity-50 transition-all"
                        >
                            <Cpu size={14} className={compiling ? "animate-spin text-orange-500" : ""} /> {compiling ? t('studio.btns.compiling') : t('studio.btns.compile')}
                        </button>
                        <button 
                            onClick={handleSave}
                            className="flex items-center gap-2 px-5 py-2 rounded text-[11px] font-black bg-orange-600 hover:bg-orange-500 text-white transition-all shadow-lg active:scale-95"
                        >
                            <Save size={14} /> {t('studio.btns.save_deploy')}
                        </button>
                        <div className="w-[1px] h-6 bg-slate-800 mx-2"></div>
                        <button onClick={() => navigate('/admin/workflow/studio')} className="p-2 hover:bg-red-500/10 hover:text-red-500 rounded transition-all"><X size={20}/></button>
                    </div>
                </div>

                {/* Monaco Editor Container */}
                <div className="flex-1 bg-[#0b0e14] relative" dir="ltr">
                    <Editor
                        height="100%"
                        theme="vs-dark"
                        defaultLanguage="python"
                        value={scriptData.script_content}
                        beforeMount={handleEditorWillMount}
                        onMount={(editor) => editorRef.current = editor}
                        onChange={(v) => setScriptData({...scriptData, script_content: v})}
                        options={{
                            fontSize: 15,
                            fontFamily: 'Fira Code, SFMono-Regular, Consolas, monospace',
                            minimap: { enabled: true },
                            automaticLayout: true,
                            scrollBeyondLastLine: false,
                            lineNumbers: 'on',
                            renderLineHighlight: 'all',
                            padding: { top: 20 },
                            bracketPairColorization: { enabled: true },
                            cursorSmoothCaretAnimation: "on",
                            smoothScrolling: true
                        }}
                    />
                </div>

                {/* Console / Output Area */}
                <div className="h-48 bg-[#0b0e14] border-t border-slate-800 flex flex-col shadow-inner">
                    <div className={`p-2 bg-[#141923] border-b border-slate-800 flex justify-between items-center px-4 `}>
                        <div className={`flex items-center gap-2 `}>
                            <Terminal size={12} className="text-orange-500" />
                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{t('studio.console_title')}</span>
                        </div>
                        <button onClick={() => setConsoleLogs([])} className="text-[9px] font-black text-slate-600 hover:text-slate-400 uppercase">{t('studio.clear_console')}</button>
                    </div>
                    <div className={`p-4 font-mono text-[11px] overflow-y-auto custom-scrollbar flex-1 bg-black/20 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {consoleLogs.map((log, i) => (
                            <div key={i} className={`mb-1 flex gap-3 `}>
                                <span className="text-slate-600">[{log.time}]</span>
                                <span className={
                                    log.type === 'error' ? 'text-red-500' : 
                                    log.type === 'success' ? 'text-green-500' : 'text-blue-400'
                                }>
                                    {log.type === 'error' ? '✖' : log.type === 'success' ? '✔' : 'ℹ'} {log.msg}
                                </span>
                            </div>
                        ))}
                        {compiling && <div className="text-orange-500 animate-pulse tracking-widest mt-2 uppercase text-[10px]">>> {t('studio.console.analyzing')}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActionScriptStudio;